import {
  ModelFigureType,
  isImageTaskProcessingStatus,
  isImageTaskSucceededStatus,
} from "../constants"
import type { UploadedResource } from "../media/uploadedResources"
import { linesToList } from "../utils"
import { getVideoDurationOptions, VIDEO_CHANNEL_OPTIONS } from "./videoModels"
import { getStoryboardHasVoiceover, parseStoryboardPayload } from "./storyboard"

export const getRecreateImageUrls = (value: string): string[] => {
  return Array.from(new Set(linesToList(value)))
}

export const RECREATE_IMAGE_LIMIT = Math.max(
  ...VIDEO_CHANNEL_OPTIONS.map(({ maxImageCount }) => maxImageCount),
)
const VIDEO_DURATIONS = VIDEO_CHANNEL_OPTIONS.flatMap(({ value }) =>
  getVideoDurationOptions(value),
)
export const RECREATE_VIDEO_RANGE = {
  MIN: Math.min(...VIDEO_DURATIONS),
  MAX: Math.max(...VIDEO_DURATIONS),
} as const
export const RECREATE_AUDIO_DURATION = 15
export const DEFAULT_BACKGROUND_MUSIC_MODE = "generate" as const

export type RecreateSystemModel = Pick<API.Model.ModelFigure, "model_id"> &
  Partial<
    Pick<
      API.Model.ModelFigure,
      "name" | "display_url" | "lineart_url" | "images"
    >
  >

export type RecreateModel =
  | { mode: "none" }
  | { mode: "system"; model: RecreateSystemModel | null }
  | {
      mode: "custom"
      url: string
      artline?: API.Task.ModelFigureArtlineInfo | null
    }
export type RecreateAudio = UploadedResource & {
  duration?: number
  trimmed: boolean
}

export const getRecreateDurationError = (duration?: number): string => {
  if (duration === undefined || !Number.isFinite(duration) || duration <= 0) {
    return "无法读取视频时长，请重试或重新上传"
  }
  if (duration < RECREATE_VIDEO_RANGE.MIN) {
    return `参考视频不足 ${RECREATE_VIDEO_RANGE.MIN} 秒，请重新上传`
  }
  if (duration > RECREATE_VIDEO_RANGE.MAX) {
    return `参考视频超过 ${RECREATE_VIDEO_RANGE.MAX} 秒，请先截取 ${RECREATE_VIDEO_RANGE.MIN}～${RECREATE_VIDEO_RANGE.MAX} 秒的片段`
  }
  return ""
}

type RecreateReferences = {
  referenceAudio: RecreateAudio | null
  resourcesPending: boolean
  referenceModel: RecreateModel
  savedReferenceModel?: RecreateModel | null
  isGeneratingModelArtline?: boolean
  modelArtlineError?: string
}

export type RecreateReferenceSettings = {
  video_url: string
  start_time: number
  end_time: number
  model_figure: API.FrameCopy.ModelFigureInfo | null
  timbre_url: string
}

export const getRecreateModelFigure = (
  model: RecreateModel,
): API.FrameCopy.ModelFigureInfo | null => {
  if (model.mode === "system" && model.model?.model_id) {
    return {
      type: ModelFigureType.system,
      model_id: model.model.model_id,
      model_url: "",
    }
  }
  if (model.mode === "custom" && model.url.trim()) {
    return {
      type: ModelFigureType.manual,
      model_id: "",
      model_url: model.url.trim(),
    }
  }
  return null
}

export const getRecreateModelImageUrl = (model: RecreateModel): string => {
  if (model.mode === "custom") {
    const artline = getRecreateModelArtline(model)
    return artline && isImageTaskSucceededStatus(artline.status)
      ? artline.result_urls[0] || model.url
      : model.url
  }
  if (model.mode === "system")
    return model.model?.lineart_url || model.model?.images?.lineart?.url || ""
  return ""
}

export const getRecreateModelArtline = (
  model: RecreateModel,
): API.Task.ModelFigureArtlineInfo | null => {
  if (model.mode !== "custom" || !model.artline) return null
  return getRecreateArtlineSourceUrl(model.artline) === model.url
    ? model.artline
    : null
}

export const getRecreateArtlineSourceUrl = (
  artline?: API.Task.ModelFigureArtlineInfo | null,
): string => {
  if (!artline) return ""
  try {
    const params: unknown = JSON.parse(artline.create_params)
    if (!params || typeof params !== "object" || !("image_urls" in params))
      return ""
    const urls = params.image_urls
    return Array.isArray(urls) &&
      urls.length === 1 &&
      typeof urls[0] === "string"
      ? urls[0].trim()
      : ""
  } catch {
    return ""
  }
}

export const getRecreateArtlineInputError = (state: {
  resourcesPending?: boolean
  referenceModel: RecreateModel
}): string => {
  if (state.resourcesPending) return "素材正在上传或处理中，请稍候"
  if (
    state.referenceModel.mode !== "custom" ||
    !state.referenceModel.url.trim()
  )
    return "请先上传自定义模特参考图"
  return ""
}

export const getRecreateInputError = ({
  resourcesPending,
  isGeneratingModelArtline,
}: RecreateReferences): string => {
  if (isGeneratingModelArtline) return "模特线稿图正在生成中，请稍候"
  if (resourcesPending) return "素材正在上传或处理中，请稍候"
  return ""
}

export const getRecreateGenerationAudio = ({
  replicaPrompt = "",
  referenceAudio,
}: {
  replicaPrompt?: string
  referenceAudio: RecreateAudio | null
}): RecreateAudio | null => {
  const payload = parseStoryboardPayload(replicaPrompt)
  // 无法解析的历史文本保留用户的音色选择，不猜测是否包含口播。
  return payload && !getStoryboardHasVoiceover(payload.rows)
    ? null
    : referenceAudio
}

export const getRecreateGenerationResources = ({
  videoImageUrls,
  referenceModel,
  savedReferenceModel,
  referenceAudio,
  replicaPrompt,
}: {
  videoImageUrls: string
  replicaPrompt?: string
} & Pick<
  RecreateReferences,
  "referenceModel" | "savedReferenceModel" | "referenceAudio"
>): UploadedResource[] => {
  const images = getRecreateImageUrls(videoImageUrls)
  const audio = getRecreateGenerationAudio({ replicaPrompt, referenceAudio })
  // create-video 沿用任务中已保存的模特，左侧重新选择不会更新后端。
  const modelUrl = getRecreateModelImageUrl(
    savedReferenceModel ?? referenceModel,
  )
  if (modelUrl && !images.includes(modelUrl)) images.push(modelUrl)
  return [
    ...images.map((url, index) => ({
      url,
      name: `图片${index + 1}`,
      type: "image/jpeg",
      uploadedAt: index,
    })),
    ...(audio ? [{ ...audio, type: audio.type || "audio/wav" }] : []),
  ]
}

export const getRecreateReferenceSettings = (
  state: {
    videoUrl: string
    start_time: number
    end_time: number
  } & Pick<RecreateReferences, "referenceModel" | "referenceAudio">,
): RecreateReferenceSettings => ({
  video_url: state.videoUrl.trim(),
  start_time: state.start_time,
  end_time: state.end_time,
  model_figure: getRecreateModelFigure(state.referenceModel),
  timbre_url: state.referenceAudio?.url || "",
})

export const getRecreateGenerationError = (
  state: {
    savedReferenceSettings: RecreateReferenceSettings | null
  } & RecreateReferences,
): string => {
  const inputError = getRecreateInputError(state)
  if (inputError) return inputError
  if (state.modelArtlineError) return state.modelArtlineError
  const model = state.savedReferenceModel ?? state.referenceModel
  if (model.mode === "custom" && model.artline) {
    const artline = getRecreateModelArtline(model)
    if (!artline) return "模特图片已变更，请重新生成线稿图"
    if (isImageTaskProcessingStatus(artline.status))
      return "模特线稿图正在生成中，请稍候"
    if (
      !isImageTaskSucceededStatus(artline.status) ||
      !artline.result_urls.length
    )
      return artline.error_message || "模特线稿图尚未生成成功，请重新转换"
  }
  if (
    model.mode === "system" &&
    model.model?.model_id &&
    !getRecreateModelImageUrl(model)
  ) {
    return "无法读取系统模特参考图，请重新选择模特"
  }
  if (!state.savedReferenceSettings) return "请先生成视频脚本"
  return ""
}

export const getRecreateReferenceChanges = (
  state: Parameters<typeof getRecreateReferenceSettings>[0] & {
    videoDuration?: number
    savedReferenceSettings: RecreateReferenceSettings | null
  },
): string[] => {
  const saved = state.savedReferenceSettings
  if (!saved) return []
  const current = getRecreateReferenceSettings(state)
  const sameRange =
    saved.end_time === 0
      ? current.start_time === 0 &&
        (current.end_time === 0 || current.end_time === state.videoDuration)
      : current.start_time === saved.start_time &&
        current.end_time === saved.end_time
  const changes: string[] = []
  if (current.video_url !== saved.video_url) changes.push("参考视频")
  if (!sameRange) changes.push("截取范围")
  if (
    JSON.stringify(current.model_figure) !== JSON.stringify(saved.model_figure)
  )
    changes.push("模特")
  return changes
}
